'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ScoreGauge from '@/components/tools/ScoreGauge';
import ProgressBar from '@/components/tools/ProgressBar';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type CheckStatus = 'pass' | 'warning' | 'fail';

interface FormattingCheck {
  id: string;
  label: string;
  status: CheckStatus;
  score: number; // 0-100
  findings: string[];
}

interface FormattingResult {
  overallScore: number;
  checks: FormattingCheck[];
  tips: string[];
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const EXPECTED_SECTIONS_ORDERED = [
  'contact',
  'summary',
  'objective',
  'experience',
  'education',
  'skills',
  'certifications',
  'projects',
  'awards',
  'volunteer',
  'publications',
  'languages',
  'interests',
  'references',
];

const SECTION_HEADER_PATTERNS: Record<string, RegExp[]> = {
  contact: [/^(contact\s*(info|information|details)?)\s*$/i],
  summary: [
    /^(professional\s+)?summary$/i,
    /^profile$/i,
    /^about(\s+me)?$/i,
    /^career\s+summary$/i,
    /^executive\s+summary$/i,
  ],
  objective: [
    /^(career\s+)?objective$/i,
    /^career\s+goal$/i,
  ],
  experience: [
    /^(work\s+|professional\s+)?experience$/i,
    /^employment(\s+history)?$/i,
    /^work\s+history$/i,
    /^professional\s+background$/i,
  ],
  education: [
    /^education(al\s+background)?$/i,
    /^academic\s+(background|history)$/i,
  ],
  skills: [
    /^(technical\s+|core\s+|key\s+)?skills$/i,
    /^(core\s+)?competencies$/i,
    /^proficiencies$/i,
    /^areas\s+of\s+expertise$/i,
    /^tools\s*(&|and)\s*technologies$/i,
  ],
  certifications: [
    /^certification(s)?$/i,
    /^certificate(s)?$/i,
    /^licenses?\s*(&|and)\s*certifications?$/i,
    /^professional\s+development$/i,
  ],
  projects: [/^(key\s+|selected\s+)?projects?$/i],
  awards: [
    /^awards?(\s+(&|and)\s+honors?)?$/i,
    /^honors?(\s+(&|and)\s+awards?)?$/i,
    /^achievements?$/i,
    /^accomplishments?$/i,
  ],
  volunteer: [
    /^volunteer(ing)?(\s+(experience|work))?$/i,
    /^community\s+(service|involvement)$/i,
  ],
  publications: [/^publications?$/i, /^research$/i],
  languages: [/^languages?$/i],
  interests: [/^interests?$/i, /^hobbies(\s+(&|and)\s+interests?)?$/i],
  references: [/^references?$/i],
};

const BULLET_CHARS = ['-', '*', '\u2022', '\u25CF', '\u25CB', '\u2023', '\u2043', '\u25AA', '\u25AB', '\u2013', '\u2014', '\u27A4', '>'];

const ATS_PROBLEM_CHARS = /[\u00A0\u2018\u2019\u201C\u201D\u2026\u2028\u2029\u00AD\u200B\u200C\u200D\uFEFF\u00AB\u00BB\u00B7\u2039\u203A\u2060\u00AE\u2122\u00A9\u00B0\u00BC\u00BD\u00BE\u00D7\u00F7\u221A\u221E\u2264\u2265\u2260\u2248]/g;

const ATS_CHAR_NAMES: Record<string, string> = {
  '\u00A0': 'non-breaking space',
  '\u2018': 'left single curly quote',
  '\u2019': 'right single curly quote / apostrophe',
  '\u201C': 'left double curly quote',
  '\u201D': 'right double curly quote',
  '\u2026': 'ellipsis character',
  '\u2028': 'line separator',
  '\u2029': 'paragraph separator',
  '\u00AD': 'soft hyphen',
  '\u200B': 'zero-width space',
  '\u200C': 'zero-width non-joiner',
  '\u200D': 'zero-width joiner',
  '\uFEFF': 'byte order mark',
  '\u00AB': 'left-pointing double angle quote',
  '\u00BB': 'right-pointing double angle quote',
  '\u00B7': 'middle dot',
  '\u2039': 'single left-pointing angle quote',
  '\u203A': 'single right-pointing angle quote',
  '\u2060': 'word joiner',
  '\u00AE': 'registered sign',
  '\u2122': 'trademark sign',
  '\u00A9': 'copyright sign',
  '\u00B0': 'degree sign',
  '\u00BC': 'fraction 1/4',
  '\u00BD': 'fraction 1/2',
  '\u00BE': 'fraction 3/4',
  '\u00D7': 'multiplication sign',
  '\u00F7': 'division sign',
  '\u221A': 'square root',
  '\u221E': 'infinity',
  '\u2264': 'less-than-or-equal',
  '\u2265': 'greater-than-or-equal',
  '\u2260': 'not-equal',
  '\u2248': 'approximately equal',
};

/* ------------------------------------------------------------------ */
/*  1. Section Headers Check                                           */
/* ------------------------------------------------------------------ */

function checkSectionHeaders(lines: string[]): FormattingCheck {
  const findings: string[] = [];
  const foundSections: { key: string; index: number; name: string }[] = [];

  lines.forEach((line, i) => {
    const trimmed = line.trim().replace(/[:\-|_]/g, '').trim();
    if (trimmed.length === 0 || trimmed.split(/\s+/).length > 6) return;

    for (const [key, patterns] of Object.entries(SECTION_HEADER_PATTERNS)) {
      for (const pat of patterns) {
        if (pat.test(trimmed)) {
          foundSections.push({ key, index: i, name: line.trim() });
          return;
        }
      }
    }

    // Also detect ALL CAPS lines of 1-5 words as likely headers
    const wordCount = trimmed.split(/\s+/).length;
    if (
      wordCount <= 5 &&
      trimmed.length > 2 &&
      line.trim() === line.trim().toUpperCase() &&
      /[A-Z]/.test(line)
    ) {
      // Try to match to a known section
      for (const [key, patterns] of Object.entries(SECTION_HEADER_PATTERNS)) {
        for (const pat of patterns) {
          if (pat.test(trimmed)) {
            foundSections.push({ key, index: i, name: line.trim() });
            return;
          }
        }
      }
    }
  });

  const foundKeys = foundSections.map((s) => s.key);
  const uniqueKeys = [...new Set(foundKeys)];

  // Check essential sections
  const essentialSections = ['experience', 'education', 'skills'];
  const missingSections: string[] = [];
  for (const essential of essentialSections) {
    if (!uniqueKeys.includes(essential)) {
      missingSections.push(essential);
    }
  }

  // Check ordering
  let outOfOrder = false;
  if (foundSections.length >= 2) {
    const sectionOrder = foundSections.map((s) =>
      EXPECTED_SECTIONS_ORDERED.indexOf(s.key),
    );
    // Filter out unknown (-1) sections
    const knownOrder = sectionOrder.filter((o) => o >= 0);
    for (let i = 1; i < knownOrder.length; i++) {
      if (knownOrder[i] < knownOrder[i - 1]) {
        outOfOrder = true;
        break;
      }
    }
  }

  // Build findings
  if (foundSections.length === 0) {
    findings.push('No standard section headers detected. ATS systems rely on headers like Experience, Education, and Skills to parse your resume.');
  } else {
    findings.push(
      `Found ${foundSections.length} section header(s): ${foundSections.map((s) => s.name).join(', ')}.`,
    );
  }

  if (missingSections.length > 0) {
    findings.push(
      `Missing essential section(s): ${missingSections.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}. Most ATS systems expect these.`,
    );
  }

  if (outOfOrder) {
    findings.push(
      'Section order may be unconventional. Standard order is: Summary, Experience, Education, Skills, then optional sections.',
    );
  }

  const hasSummary = uniqueKeys.includes('summary') || uniqueKeys.includes('objective');
  if (!hasSummary && foundSections.length > 0) {
    findings.push(
      'Consider adding a Professional Summary section at the top to give recruiters a quick overview.',
    );
  }

  // Score
  let score = 100;
  if (foundSections.length === 0) score = 10;
  else if (foundSections.length < 3) score = 40;
  else if (foundSections.length < 4) score = 60;

  score -= missingSections.length * 15;
  if (outOfOrder) score -= 10;
  if (!hasSummary) score -= 5;

  score = Math.max(0, Math.min(100, score));

  let status: CheckStatus = 'pass';
  if (score < 50) status = 'fail';
  else if (score < 75) status = 'warning';

  return { id: 'sections', label: 'Section Headers', status, score, findings };
}

/* ------------------------------------------------------------------ */
/*  2. Bullet Point Consistency Check                                  */
/* ------------------------------------------------------------------ */

function checkBulletPoints(lines: string[]): FormattingCheck {
  const findings: string[] = [];
  const bulletTypes = new Map<string, number>();
  let bulletLineCount = 0;
  const nonEmptyLines = lines.filter((l) => l.trim().length > 0);

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;

    // Check for bullet character
    for (const char of BULLET_CHARS) {
      if (trimmed.startsWith(char)) {
        bulletTypes.set(char, (bulletTypes.get(char) || 0) + 1);
        bulletLineCount++;
        break;
      }
    }

    // Check for numbered lists
    if (/^\d+[.)]\s/.test(trimmed)) {
      bulletTypes.set('numbered', (bulletTypes.get('numbered') || 0) + 1);
      bulletLineCount++;
    }
  }

  const bulletRatio = nonEmptyLines.length > 0 ? bulletLineCount / nonEmptyLines.length : 0;

  if (bulletLineCount === 0) {
    findings.push(
      'No bullet points detected. Bullet points improve readability and help ATS systems parse individual achievements.',
    );
  } else {
    findings.push(`Found ${bulletLineCount} bullet-pointed line(s) (${Math.round(bulletRatio * 100)}% of content).`);
  }

  // Check consistency
  if (bulletTypes.size > 1) {
    const types = [...bulletTypes.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([char, count]) => {
        const name =
          char === '-'
            ? 'hyphen (-)'
            : char === '*'
              ? 'asterisk (*)'
              : char === '\u2022'
                ? 'bullet (\\u2022)'
                : char === 'numbered'
                  ? 'numbered list'
                  : `"${char}"`;
        return `${name}: ${count}`;
      });
    findings.push(
      `Mixed bullet styles detected: ${types.join(', ')}. Use a single consistent style for ATS compatibility.`,
    );
  } else if (bulletTypes.size === 1) {
    findings.push('Bullet style is consistent throughout the resume.');
  }

  if (bulletRatio < 0.15 && bulletLineCount > 0) {
    findings.push(
      'Low bullet-point density. Aim for 30-65% of content lines as bullets for best scannability.',
    );
  } else if (bulletRatio > 0.8) {
    findings.push(
      'Very high bullet density. Ensure you have section headers, job titles, and context beyond just bullet points.',
    );
  }

  // Score
  let score = 100;
  if (bulletLineCount === 0) score = 20;
  else if (bulletRatio < 0.15) score = 50;
  else if (bulletRatio > 0.8) score = 60;

  if (bulletTypes.size > 2) score -= 25;
  else if (bulletTypes.size > 1) score -= 15;

  score = Math.max(0, Math.min(100, score));

  let status: CheckStatus = 'pass';
  if (score < 50) status = 'fail';
  else if (score < 75) status = 'warning';

  return { id: 'bullets', label: 'Bullet Point Consistency', status, score, findings };
}

/* ------------------------------------------------------------------ */
/*  3. Line Length Consistency Check                                    */
/* ------------------------------------------------------------------ */

function checkLineLengths(lines: string[]): FormattingCheck {
  const findings: string[] = [];
  const contentLines = lines.filter((l) => l.trim().length > 0);

  if (contentLines.length === 0) {
    return {
      id: 'linelength',
      label: 'Line Length Consistency',
      status: 'fail',
      score: 0,
      findings: ['No content found to analyze.'],
    };
  }

  const lengths = contentLines.map((l) => l.trim().length);
  const avgLength = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const maxLength = Math.max(...lengths);
  const minLength = Math.min(...lengths);

  const veryLongLines = lengths.filter((l) => l > 120).length;
  const veryShortLines = lengths.filter((l) => l < 10 && l > 0).length;

  findings.push(
    `Average line length: ${Math.round(avgLength)} characters (range: ${minLength}-${maxLength}).`,
  );

  if (veryLongLines > 0) {
    findings.push(
      `${veryLongLines} line(s) exceed 120 characters. Very long lines may wrap unpredictably in ATS systems or printed resumes.`,
    );
  }

  if (veryShortLines > 3) {
    findings.push(
      `${veryShortLines} very short lines detected (under 10 characters). These may indicate fragmented formatting or incomplete content.`,
    );
  }

  // Check for variance (standard deviation)
  const variance =
    lengths.reduce((sum, l) => sum + Math.pow(l - avgLength, 2), 0) / lengths.length;
  const stdDev = Math.sqrt(variance);

  if (stdDev > 50) {
    findings.push(
      'High variation in line lengths. This can indicate inconsistent formatting across different sections.',
    );
  } else if (stdDev < 35) {
    findings.push('Line lengths are reasonably consistent across the resume.');
  }

  // Score
  let score = 100;
  if (veryLongLines > 5) score -= 30;
  else if (veryLongLines > 2) score -= 15;
  else if (veryLongLines > 0) score -= 5;

  if (veryShortLines > 5) score -= 15;
  else if (veryShortLines > 3) score -= 10;

  if (stdDev > 60) score -= 20;
  else if (stdDev > 50) score -= 10;

  score = Math.max(0, Math.min(100, score));

  let status: CheckStatus = 'pass';
  if (score < 50) status = 'fail';
  else if (score < 75) status = 'warning';

  return { id: 'linelength', label: 'Line Length Consistency', status, score, findings };
}

/* ------------------------------------------------------------------ */
/*  4. Spacing and Whitespace Patterns                                 */
/* ------------------------------------------------------------------ */

function checkSpacing(lines: string[]): FormattingCheck {
  const findings: string[] = [];

  const blankLineCount = lines.filter((l) => l.trim().length === 0).length;
  const totalLines = lines.length;
  const blankRatio = totalLines > 0 ? blankLineCount / totalLines : 0;

  // Check for consecutive blank lines (double-spacing)
  let doubleBlankCount = 0;
  let tripleBlankCount = 0;
  let consecutiveBlank = 0;
  for (const line of lines) {
    if (line.trim().length === 0) {
      consecutiveBlank++;
      if (consecutiveBlank === 2) doubleBlankCount++;
      if (consecutiveBlank === 3) tripleBlankCount++;
    } else {
      consecutiveBlank = 0;
    }
  }

  // Check for long blocks without any spacing
  let maxConsecutiveContent = 0;
  let currentConsecutive = 0;
  for (const line of lines) {
    if (line.trim().length > 0) {
      currentConsecutive++;
      maxConsecutiveContent = Math.max(maxConsecutiveContent, currentConsecutive);
    } else {
      currentConsecutive = 0;
    }
  }

  // Check for leading whitespace inconsistency
  const indentLevels = new Set<number>();
  for (const line of lines) {
    if (line.trim().length === 0) continue;
    const leading = line.match(/^(\s*)/)?.[1].length || 0;
    if (leading > 0) indentLevels.add(leading);
  }

  // Check for tab characters
  const hasTabChars = lines.some((l) => l.includes('\t'));

  // Findings
  findings.push(
    `${blankLineCount} blank line(s) out of ${totalLines} total (${Math.round(blankRatio * 100)}% whitespace ratio).`,
  );

  if (blankRatio < 0.08) {
    findings.push(
      'Resume appears very dense with minimal blank lines. Add spacing between sections for better readability.',
    );
  } else if (blankRatio >= 0.15 && blankRatio <= 0.35) {
    findings.push('Whitespace ratio is in a good range for readability.');
  } else if (blankRatio > 0.4) {
    findings.push(
      'Excessive blank lines detected. Too much whitespace may indicate poor use of space or formatting issues.',
    );
  }

  if (tripleBlankCount > 0) {
    findings.push(
      `${tripleBlankCount} instance(s) of 3+ consecutive blank lines. This wastes valuable space and may confuse ATS parsers.`,
    );
  } else if (doubleBlankCount > 3) {
    findings.push(
      `${doubleBlankCount} instance(s) of consecutive blank lines. A single blank line between sections is sufficient.`,
    );
  }

  if (maxConsecutiveContent > 10) {
    findings.push(
      `A block of ${maxConsecutiveContent} consecutive content lines without any blank lines was detected. Break up dense blocks with spacing.`,
    );
  }

  if (hasTabChars) {
    findings.push(
      'Tab characters detected. Tabs can render inconsistently across ATS systems. Use spaces for indentation.',
    );
  }

  if (indentLevels.size > 3) {
    findings.push(
      `${indentLevels.size} different indentation levels found. Keep indentation consistent (1-2 levels max) for clean parsing.`,
    );
  }

  // Score
  let score = 100;
  if (blankRatio < 0.08) score -= 25;
  else if (blankRatio > 0.4) score -= 20;

  if (tripleBlankCount > 0) score -= 15;
  if (doubleBlankCount > 3) score -= 10;
  if (maxConsecutiveContent > 10) score -= 15;
  if (hasTabChars) score -= 10;
  if (indentLevels.size > 4) score -= 10;
  else if (indentLevels.size > 3) score -= 5;

  score = Math.max(0, Math.min(100, score));

  let status: CheckStatus = 'pass';
  if (score < 50) status = 'fail';
  else if (score < 75) status = 'warning';

  return { id: 'spacing', label: 'Spacing & Whitespace', status, score, findings };
}

/* ------------------------------------------------------------------ */
/*  5. Contact Information Check                                       */
/* ------------------------------------------------------------------ */

function checkContactInfo(text: string, lines: string[]): FormattingCheck {
  const findings: string[] = [];
  const topLines = lines.slice(0, 8).join('\n');

  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  const phoneRegex = /(\+?\d{1,3}[\s.-]?)?\(?\d{2,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}/;
  const linkedinRegex = /linkedin\.com\/in\/[a-zA-Z0-9_-]+/i;
  const urlRegex = /https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?/i;
  const locationRegex = /\b[A-Z][a-z]+,\s*[A-Z]{2}\b|\b[A-Z][a-z]+,\s*[A-Z][a-z]+\b/;

  const hasEmail = emailRegex.test(text);
  const hasPhone = phoneRegex.test(text);
  const hasLinkedin = linkedinRegex.test(text);
  const hasUrl = urlRegex.test(text);
  const hasLocation = locationRegex.test(text);

  // Check if contact info is near the top
  const emailInTop = emailRegex.test(topLines);
  const phoneInTop = phoneRegex.test(topLines);

  let foundCount = 0;

  if (hasEmail) {
    foundCount++;
    if (emailInTop) {
      findings.push('Email address found near the top of the resume.');
    } else {
      findings.push('Email address found but not near the top. Place contact info in the first few lines for easy ATS extraction.');
    }
  } else {
    findings.push('No email address detected. This is essential for recruiter contact.');
  }

  if (hasPhone) {
    foundCount++;
    if (phoneInTop) {
      findings.push('Phone number found near the top.');
    } else {
      findings.push('Phone number found but not in the header area.');
    }
  } else {
    findings.push('No phone number detected. Consider adding one for direct contact.');
  }

  if (hasLinkedin) {
    foundCount++;
    findings.push('LinkedIn profile URL detected.');
  } else if (hasUrl) {
    foundCount++;
    findings.push('A URL was detected (portfolio or personal site). Consider also adding your LinkedIn profile.');
  } else {
    findings.push('No LinkedIn profile or portfolio URL found. Adding one can strengthen your application.');
  }

  if (hasLocation) {
    foundCount++;
    findings.push('Location/city detected in the resume.');
  } else {
    findings.push('No city or location detected. Some employers filter by location, so consider including it.');
  }

  // Check if the first line looks like a name
  const firstNonEmpty = lines.find((l) => l.trim().length > 0);
  if (firstNonEmpty) {
    const words = firstNonEmpty.trim().split(/\s+/);
    if (words.length >= 2 && words.length <= 4 && !emailRegex.test(firstNonEmpty) && !phoneRegex.test(firstNonEmpty)) {
      findings.push('First line appears to be a name -- good placement.');
    } else if (words.length === 1 && words[0].length > 1) {
      findings.push('First line may be a single name. Full name (first and last) is recommended.');
    }
  }

  // Score
  let score = 0;
  if (hasEmail) score += 30;
  if (hasPhone) score += 25;
  if (hasLinkedin || hasUrl) score += 20;
  if (hasLocation) score += 10;
  if (emailInTop && phoneInTop) score += 15;
  else if (emailInTop || phoneInTop) score += 8;

  score = Math.max(0, Math.min(100, score));

  let status: CheckStatus = 'pass';
  if (score < 50) status = 'fail';
  else if (score < 75) status = 'warning';

  return { id: 'contact', label: 'Contact Information', status, score, findings };
}

/* ------------------------------------------------------------------ */
/*  6. Date Format Consistency                                         */
/* ------------------------------------------------------------------ */

function checkDateFormats(text: string): FormattingCheck {
  const findings: string[] = [];

  // Various date format patterns
  const datePatterns: { name: string; regex: RegExp }[] = [
    { name: 'Mon YYYY', regex: /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}\b/g },
    { name: 'Month YYYY', regex: /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\b/g },
    { name: 'MM/YYYY', regex: /\b(0?[1-9]|1[0-2])\/\d{4}\b/g },
    { name: 'MM-YYYY', regex: /\b(0?[1-9]|1[0-2])-\d{4}\b/g },
    { name: 'YYYY', regex: /(?<![/\d-])\b(19|20)\d{2}\b(?![/\d-])/g },
    { name: 'MM/DD/YYYY', regex: /\b(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}\b/g },
  ];

  const formatsFound = new Map<string, number>();

  for (const { name, regex } of datePatterns) {
    const matches = text.match(regex);
    if (matches && matches.length > 0) {
      formatsFound.set(name, matches.length);
    }
  }

  // Check for "Present" / "Current"
  const presentRegex = /\b(present|current|now|ongoing)\b/gi;
  const presentMatches = text.match(presentRegex);
  if (presentMatches) {
    formatsFound.set('Present/Current', presentMatches.length);
  }

  // Check for date ranges
  const dateRangeRegex = /\b\d{4}\s*[-\u2013\u2014]\s*(present|current|\d{4})\b/gi;
  const dateRanges = text.match(dateRangeRegex);

  if (formatsFound.size === 0) {
    findings.push(
      'No date formats detected. Include dates for your work experience and education to show your timeline.',
    );
  } else {
    const formatList = [...formatsFound.entries()]
      .map(([fmt, count]) => `${fmt} (${count}x)`)
      .join(', ');
    findings.push(`Date formats found: ${formatList}.`);
  }

  // Check consistency (excluding "Present/Current" and standalone YYYY)
  const mainFormats = [...formatsFound.keys()].filter(
    (f) => f !== 'Present/Current' && f !== 'YYYY',
  );

  if (mainFormats.length > 1) {
    findings.push(
      `Multiple date formats detected (${mainFormats.join(', ')}). Use a single consistent format like "Mon YYYY" throughout.`,
    );
  } else if (mainFormats.length === 1) {
    findings.push(`Date format is consistent (${mainFormats[0]}).`);
  }

  if (dateRanges && dateRanges.length > 0) {
    findings.push(`${dateRanges.length} date range(s) detected (e.g., "2020 - Present"). Good for showing employment duration.`);
  }

  // Check for full dates (MM/DD/YYYY) which are unnecessary on resumes
  if (formatsFound.has('MM/DD/YYYY')) {
    findings.push(
      'Full dates (MM/DD/YYYY) detected. Resumes typically only need month and year -- remove specific days.',
    );
  }

  // Score
  let score = 100;
  if (formatsFound.size === 0) {
    score = 25;
  } else {
    if (mainFormats.length > 2) score -= 30;
    else if (mainFormats.length > 1) score -= 15;

    if (formatsFound.has('MM/DD/YYYY')) score -= 15;

    if (!dateRanges || dateRanges.length === 0) score -= 10;
  }

  score = Math.max(0, Math.min(100, score));

  let status: CheckStatus = 'pass';
  if (score < 50) status = 'fail';
  else if (score < 75) status = 'warning';

  return { id: 'dates', label: 'Date Format Consistency', status, score, findings };
}

/* ------------------------------------------------------------------ */
/*  7. Special Characters (ATS-Breaking) Check                         */
/* ------------------------------------------------------------------ */

function checkSpecialCharacters(text: string): FormattingCheck {
  const findings: string[] = [];
  const problemChars = new Map<string, number>();

  let match;
  const regex = new RegExp(ATS_PROBLEM_CHARS.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    const char = match[0];
    problemChars.set(char, (problemChars.get(char) || 0) + 1);
  }

  const totalProblems = [...problemChars.values()].reduce((a, b) => a + b, 0);

  if (totalProblems === 0) {
    findings.push(
      'No problematic special characters detected. Your resume text uses ATS-safe characters.',
    );
  } else {
    findings.push(
      `Found ${totalProblems} potentially problematic character(s) across ${problemChars.size} type(s).`,
    );

    // List up to 6 most common problem chars
    const sorted = [...problemChars.entries()].sort((a, b) => b[1] - a[1]);
    for (const [char, count] of sorted.slice(0, 6)) {
      const name = ATS_CHAR_NAMES[char] || `U+${char.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')}`;
      findings.push(`"${name}" found ${count} time(s). Replace with a standard ASCII equivalent.`);
    }

    if (problemChars.has('\u2018') || problemChars.has('\u2019') || problemChars.has('\u201C') || problemChars.has('\u201D')) {
      findings.push(
        'Curly/smart quotes detected. Replace with straight quotes (\' and ") for ATS compatibility.',
      );
    }

    if (problemChars.has('\u2026')) {
      findings.push(
        'Ellipsis character (...) detected. Use three periods instead.',
      );
    }
  }

  // Score
  let score = 100;
  if (totalProblems > 20) score = 20;
  else if (totalProblems > 10) score = 45;
  else if (totalProblems > 5) score = 65;
  else if (totalProblems > 0) score = 80;

  score = Math.max(0, Math.min(100, score));

  let status: CheckStatus = 'pass';
  if (score < 50) status = 'fail';
  else if (score < 75) status = 'warning';

  return { id: 'specialchars', label: 'ATS-Safe Characters', status, score, findings };
}

/* ------------------------------------------------------------------ */
/*  8. Resume Length Check                                             */
/* ------------------------------------------------------------------ */

function checkResumeLength(text: string, lines: string[]): FormattingCheck {
  const findings: string[] = [];

  const words = text.split(/\s+/).filter((w) => w.length > 0);
  const wordCount = words.length;
  const contentLines = lines.filter((l) => l.trim().length > 0).length;
  const totalLines = lines.length;

  // Estimate pages (rough: ~400-500 words per page for a resume)
  const estimatedPages = wordCount / 450;

  findings.push(
    `Resume contains approximately ${wordCount} words, ${contentLines} content lines, and ${totalLines} total lines.`,
  );
  findings.push(
    `Estimated length: ~${estimatedPages < 0.8 ? 'less than 1 page' : estimatedPages < 1.3 ? '1 page' : estimatedPages < 1.8 ? '1-2 pages' : estimatedPages < 2.3 ? '2 pages' : '2+ pages'}.`,
  );

  if (wordCount < 150) {
    findings.push(
      'Resume appears very short. This may indicate incomplete content. Most effective resumes have 300-700 words.',
    );
  } else if (wordCount < 300) {
    findings.push(
      'Resume is on the shorter side. Consider adding more detail to your experience and accomplishments.',
    );
  } else if (wordCount >= 300 && wordCount <= 700) {
    findings.push(
      'Word count is in the ideal range for a one-page resume. Concise and focused.',
    );
  } else if (wordCount <= 1100) {
    findings.push(
      'Word count suggests a solid two-page resume. Appropriate for candidates with 10+ years of experience.',
    );
  } else {
    findings.push(
      'Resume may be too long. Unless you are a senior executive or academic, try to keep it under 2 pages (700-1100 words).',
    );
  }

  // Score
  let score: number;
  if (wordCount < 100) score = 15;
  else if (wordCount < 200) score = 40;
  else if (wordCount < 300) score = 65;
  else if (wordCount <= 700) score = 100;
  else if (wordCount <= 1100) score = 85;
  else if (wordCount <= 1400) score = 60;
  else score = 35;

  score = Math.max(0, Math.min(100, score));

  let status: CheckStatus = 'pass';
  if (score < 50) status = 'fail';
  else if (score < 75) status = 'warning';

  return { id: 'length', label: 'Resume Length', status, score, findings };
}

/* ------------------------------------------------------------------ */
/*  Main analysis function                                             */
/* ------------------------------------------------------------------ */

function analyzeFormatting(text: string): FormattingResult {
  const lines = text.split('\n');

  const checks: FormattingCheck[] = [
    checkSectionHeaders(lines),
    checkBulletPoints(lines),
    checkLineLengths(lines),
    checkSpacing(lines),
    checkContactInfo(text, lines),
    checkDateFormats(text),
    checkSpecialCharacters(text),
    checkResumeLength(text, lines),
  ];

  // Weighted overall score
  const weights: Record<string, number> = {
    sections: 0.18,
    bullets: 0.12,
    linelength: 0.08,
    spacing: 0.12,
    contact: 0.18,
    dates: 0.10,
    specialchars: 0.12,
    length: 0.10,
  };

  const overallScore = Math.round(
    checks.reduce((sum, check) => sum + check.score * (weights[check.id] || 0.125), 0),
  );

  // Generate tips
  const tips: string[] = [];

  if (overallScore >= 85) {
    tips.push(
      'Your resume formatting is strong for ATS compatibility. Focus on fine-tuning the minor issues below.',
    );
  } else if (overallScore >= 65) {
    tips.push(
      'Your resume has decent formatting but some areas need attention. Address the warnings and failures to improve ATS compatibility.',
    );
  } else {
    tips.push(
      'Your resume has significant formatting issues that could cause problems with ATS systems. Address the failures below before applying.',
    );
  }

  const failedChecks = checks.filter((c) => c.status === 'fail');
  const warningChecks = checks.filter((c) => c.status === 'warning');

  if (failedChecks.length > 0) {
    tips.push(
      `Priority fixes: ${failedChecks.map((c) => c.label).join(', ')}. These areas scored below 50% and should be addressed first.`,
    );
  }

  if (warningChecks.length > 0) {
    tips.push(
      `Also review: ${warningChecks.map((c) => c.label).join(', ')}. These areas could use improvement.`,
    );
  }

  tips.push(
    'For best ATS results, use a clean single-column layout with standard section headings and avoid tables, columns, images, or text boxes.',
  );

  tips.push(
    'Save your resume as a .docx or simple .pdf file. Avoid heavily designed PDFs -- they often fail ATS parsing.',
  );

  return {
    overallScore: Math.max(0, Math.min(100, overallScore)),
    checks,
    tips,
  };
}

/* ------------------------------------------------------------------ */
/*  Status icon component                                              */
/* ------------------------------------------------------------------ */

function StatusIcon({ status }: { status: CheckStatus }) {
  if (status === 'pass') {
    return (
      <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (status === 'warning') {
    return (
      <svg className="w-6 h-6 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    );
  }
  return (
    <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function statusLabel(status: CheckStatus): string {
  if (status === 'pass') return 'Pass';
  if (status === 'warning') return 'Warning';
  return 'Fail';
}

function statusColor(status: CheckStatus): string {
  if (status === 'pass') return 'text-green-700 bg-green-100';
  if (status === 'warning') return 'text-amber-700 bg-amber-100';
  return 'text-red-700 bg-red-100';
}

/* ------------------------------------------------------------------ */
/*  Educational content                                                */
/* ------------------------------------------------------------------ */

const FORMAT_TIPS = [
  {
    title: 'Use Standard Section Headers',
    description:
      'ATS systems look for headers like Experience, Education, and Skills. Creative headings like "Where I Have Been" will confuse the parser.',
  },
  {
    title: 'Stick to One Bullet Style',
    description:
      'Pick a single bullet character (hyphen, dot, or bullet point) and use it consistently throughout your entire resume.',
  },
  {
    title: 'Keep Lines Under 120 Characters',
    description:
      'Very long lines may wrap unpredictably in ATS systems and when printed. Break up long sentences into shorter, scannable bullets.',
  },
  {
    title: 'Use Consistent Date Formats',
    description:
      'Choose one date format (like "Jan 2023") and use it everywhere. Mixing "01/2023" with "January 2023" signals poor attention to detail.',
  },
  {
    title: 'Avoid Special Characters',
    description:
      'Smart quotes, em dashes, and special symbols may not parse correctly. Stick to standard ASCII characters.',
  },
  {
    title: 'Aim for 1-2 Pages',
    description:
      'Most resumes should be one page (300-700 words). Senior professionals can use two pages. Anything longer risks losing recruiter attention.',
  },
];

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function FormattingCheckerContent() {
  const [resumeText, setResumeText] = useState('');
  const [result, setResult] = useState<FormattingResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  function handleCheck() {
    if (resumeText.trim().length < 50) return;
    setIsAnalyzing(true);
    setTimeout(() => {
      setResult(analyzeFormatting(resumeText));
      setIsAnalyzing(false);
    }, 600);
  }

  function handleReset() {
    setResult(null);
    setResumeText('');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Resume Formatting Checker"
        description="Audit your resume formatting for ATS compatibility. Check section headers, bullet points, spacing, contact info, date formats, special characters, and length -- all in one scan."
        category="Resume Analysis"
      />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Input section */}
        {!result && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Paste Your Resume Text
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Paste your full resume below. We check 8 formatting aspects for ATS compatibility -- everything runs in your browser, nothing is sent to any server.
            </p>

            <textarea
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder={`John Doe
john.doe@email.com | (555) 123-4567 | linkedin.com/in/johndoe | Austin, TX

Professional Summary
Results-driven software engineer with 5+ years of experience building scalable web applications...

Experience
Senior Software Engineer | Acme Corp | Jan 2021 - Present
- Designed and implemented a microservices architecture serving 2M+ daily requests
- Led migration from monolithic to distributed system, reducing latency by 40%
- Mentored 4 junior developers through weekly code reviews and pair programming

Software Engineer | StartupCo | Jun 2018 - Dec 2020
- Built core product features in React and Node.js, growing MAU from 10K to 150K
- Implemented CI/CD pipeline reducing deployment time from 2 hours to 15 minutes

Education
B.S. Computer Science | University of Texas at Austin | May 2018

Skills
JavaScript, TypeScript, React, Node.js, Python, AWS, Docker, PostgreSQL`}
              className="w-full h-72 p-4 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />

            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-gray-400">
                {resumeText.trim().length > 0
                  ? `${resumeText.split(/\s+/).filter((w) => w.length > 0).length} words`
                  : 'Minimum 50 characters'}
              </span>
              <button
                onClick={handleCheck}
                disabled={resumeText.trim().length < 50 || isAnalyzing}
                className="bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAnalyzing ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Checking...
                  </>
                ) : (
                  'Check Formatting'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Results section */}
        {result && (
          <div className="space-y-8">
            {/* Overall Score Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex flex-col items-center">
                <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-4">
                  ATS Formatting Score
                </p>
                <ScoreGauge
                  score={result.overallScore}
                  label="Formatting"
                  size={220}
                />
                <p className="text-gray-600 mt-4 text-center max-w-md">
                  {result.overallScore >= 85
                    ? 'Excellent formatting! Your resume is well-structured for ATS compatibility.'
                    : result.overallScore >= 70
                      ? 'Good formatting with some areas to improve. Address the warnings below for a stronger ATS pass rate.'
                      : result.overallScore >= 50
                        ? 'Moderate formatting issues detected. Fix the failing checks to improve your chances with ATS systems.'
                        : 'Significant formatting problems found. Your resume may not parse correctly in many ATS systems. Address the issues below.'}
                </p>
                <div className="flex flex-wrap justify-center gap-4 mt-4 text-sm text-gray-500">
                  <span>
                    <strong className="text-green-600">
                      {result.checks.filter((c) => c.status === 'pass').length}
                    </strong>{' '}
                    passed
                  </span>
                  <span>
                    <strong className="text-amber-600">
                      {result.checks.filter((c) => c.status === 'warning').length}
                    </strong>{' '}
                    warnings
                  </span>
                  <span>
                    <strong className="text-red-600">
                      {result.checks.filter((c) => c.status === 'fail').length}
                    </strong>{' '}
                    failed
                  </span>
                </div>
              </div>
            </div>

            {/* Score Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Score Breakdown
              </h3>
              <div className="space-y-5">
                {result.checks.map((check) => (
                  <div key={check.id}>
                    <ProgressBar value={check.score} label={check.label} />
                  </div>
                ))}
              </div>
            </div>

            {/* Individual Check Results */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Formatting Checklist
              </h3>
              <div className="space-y-4">
                {result.checks.map((check) => (
                  <div
                    key={check.id}
                    className={`rounded-xl border p-5 ${
                      check.status === 'pass'
                        ? 'border-green-200 bg-green-50/50'
                        : check.status === 'warning'
                          ? 'border-amber-200 bg-amber-50/50'
                          : 'border-red-200 bg-red-50/50'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <StatusIcon status={check.status} />
                      <h4 className="font-bold text-gray-900 flex-1">
                        {check.label}
                      </h4>
                      <span
                        className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColor(check.status)}`}
                      >
                        {statusLabel(check.status)} ({check.score}%)
                      </span>
                    </div>
                    <ul className="space-y-1.5 ml-9">
                      {check.findings.map((finding, i) => (
                        <li key={i} className="text-sm text-gray-700 leading-relaxed flex items-start gap-2">
                          <span className="text-gray-400 mt-1.5 flex-shrink-0">
                            {check.status === 'pass' ? (
                              <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : check.status === 'fail' ? (
                              <svg className="w-3 h-3 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            )}
                          </span>
                          <span>{finding}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Actionable Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Tips to Improve Your Formatting
              </h3>
              <ul className="space-y-3">
                {result.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <span className="text-blue-800 leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Start over */}
            <div className="text-center">
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Check Another Resume
              </button>
            </div>

            {/* Educational content */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Why Resume Formatting Matters for ATS
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>Applicant Tracking Systems (ATS)</strong> parse your resume
                  into structured data -- extracting your name, contact info, work
                  history, education, and skills into database fields. If your formatting
                  confuses the parser, your information ends up in the wrong fields or
                  gets lost entirely.
                </p>
                <p>
                  Studies show that over <strong>75% of resumes</strong> are rejected by
                  ATS before a human ever reads them. While keyword matching is one
                  factor, poor formatting is another major reason resumes fail. An ATS
                  cannot read tables, multi-column layouts, headers/footers, or embedded
                  images.
                </p>

                <h4 className="text-lg font-bold text-gray-900 pt-2">
                  The 8 Formatting Checks We Run
                </h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Section Headers:</strong> Standard headings help the ATS
                    categorize your content correctly. Creative headings confuse parsers.
                  </li>
                  <li>
                    <strong>Bullet Points:</strong> Consistent bullet styles ensure each
                    achievement is parsed as a separate entry.
                  </li>
                  <li>
                    <strong>Line Length:</strong> Extremely long lines may wrap
                    unpredictably or get truncated by certain systems.
                  </li>
                  <li>
                    <strong>Spacing:</strong> Proper whitespace helps both ATS parsers
                    and human readers navigate your resume.
                  </li>
                  <li>
                    <strong>Contact Info:</strong> Email, phone, and location must be
                    easily extractable from the top of your resume.
                  </li>
                  <li>
                    <strong>Date Formats:</strong> Consistent date formatting helps the
                    ATS accurately calculate your years of experience.
                  </li>
                  <li>
                    <strong>Special Characters:</strong> Curly quotes, em dashes, and
                    other non-ASCII characters can break ATS parsing.
                  </li>
                  <li>
                    <strong>Resume Length:</strong> Too short means insufficient detail;
                    too long means poor focus. Both hurt your chances.
                  </li>
                </ul>

                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.indeed.com/career-advice/resumes-cover-letters/ats-resume" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Write an ATS-Friendly Resume</a>
                  {' · '}
                  <a href="https://www.careeronestop.org/JobSearch/Resumes/resume-guide.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop Resume Guide (U.S. DOL)</a>
                  {' · '}
                  <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Career Planning</a>
                </p>
              </div>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Want perfectly formatted resumes every time?"
              description="Superpower Resume generates ATS-optimized resumes with clean formatting, proper section structure, and consistent styling -- so you never worry about parsing errors again."
              buttonText="Create an ATS-Optimized Resume"
              buttonHref="https://app.superpowerresume.com/sign-up"
            />
          </div>
        )}

        {/* Educational section (shown when no results yet) */}
        {!result && (
          <div className="mt-12 space-y-8">
            {/* How it works */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">How It Works</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  {
                    step: '1',
                    title: 'Paste Your Resume',
                    desc: 'Copy your resume text and paste it above. We analyze plain text -- no file uploads or accounts needed.',
                  },
                  {
                    step: '2',
                    title: '8-Point Formatting Audit',
                    desc: 'We check section headers, bullet points, line lengths, spacing, contact info, dates, special characters, and length.',
                  },
                  {
                    step: '3',
                    title: 'Pass / Warning / Fail Results',
                    desc: 'Each check gets a clear status with specific findings so you know exactly what to fix.',
                  },
                  {
                    step: '4',
                    title: 'ATS Compatibility Score',
                    desc: 'Get an overall formatting score and actionable tips to make your resume ATS-friendly.',
                  },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <span className="text-sm font-extrabold text-white bg-blue-600 w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0">
                      {item.step}
                    </span>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">{item.title}</h4>
                      <p className="text-sm text-gray-600 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* What we check */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What We Check</h3>
              <p className="text-gray-600 mb-6">
                Our formatting checker evaluates eight dimensions that determine whether an ATS can correctly parse your resume:
              </p>
              <div className="space-y-4">
                {[
                  { name: 'Section Headers', weight: '18%', desc: 'Detects standard headings like Experience, Education, and Skills. Missing or creative headers confuse ATS parsers.' },
                  { name: 'Bullet Point Consistency', weight: '12%', desc: 'Checks that you use a single bullet style throughout. Mixed styles (hyphens + dots + arrows) cause parsing issues.' },
                  { name: 'Line Length', weight: '8%', desc: 'Flags lines over 120 characters and very short fragments. Extreme lengths cause wrapping or truncation.' },
                  { name: 'Spacing & Whitespace', weight: '12%', desc: 'Evaluates blank line distribution, consecutive blank lines, dense blocks, tab usage, and indentation consistency.' },
                  { name: 'Contact Information', weight: '18%', desc: 'Verifies email, phone, LinkedIn, and location are present and positioned near the top for easy extraction.' },
                  { name: 'Date Format Consistency', weight: '10%', desc: 'Checks that all dates use the same format. Mixed formats make it harder for ATS to calculate your experience.' },
                  { name: 'ATS-Safe Characters', weight: '12%', desc: 'Scans for smart quotes, em dashes, ellipsis characters, and other non-ASCII text that can break ATS parsing.' },
                  { name: 'Resume Length', weight: '10%', desc: 'Checks word count and estimated page length. Too short or too long both reduce your chances.' },
                ].map((item) => (
                  <div key={item.name} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-lg whitespace-nowrap">
                      {item.weight}
                    </span>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">{item.name}</h4>
                      <p className="text-sm text-gray-600 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-blue-900 mb-6 flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                ATS Formatting Best Practices
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                {FORMAT_TIPS.map((tip, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-blue-100">
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">{tip.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <ToolCTA
              headline="Skip the manual formatting -- let AI handle it"
              description="Superpower Resume generates perfectly formatted, ATS-optimized resumes in minutes. Clean structure, consistent styling, and no parsing errors."
              buttonText="Build My Resume"
              buttonHref="https://app.superpowerresume.com/sign-up"
            />
          </div>
        )}
      </div>
    </div>
  );
}
